import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import DataTable from '../components/ResponsiveDataTable';
import { getCrmHistory, searchCrmAssignableStaff } from '../config/api';
import useIsMobile from '../hooks/useIsMobile';

const INITIAL_FILTERS = {
  lead_id: '',
  entity: '',
  field: '',
  actor_id: '',
  date_from: '',
  date_to: '',
};

const ENTITY_OPTIONS = [
  ['lead', 'Lead'],
  ['contact', 'Contact'],
  ['external_id', 'Linked identifier'],
  ['hub_link', 'Hub link'],
  ['variable_value', 'Variable'],
];

const humanize = (value) => String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const displayValue = (value) => {
  if (value == null || value === '') return '—';
  const text = String(value);
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === 'object') {
      return Object.entries(parsed)
        .map(([key, item]) => `${humanize(key)}: ${Array.isArray(item) ? item.join(', ') : String(item ?? '—')}`)
        .join(' · ');
    }
  } catch (_) {
    // Plain audit values are expected and displayed as-is.
  }
  return text;
};

const CrmHistory = () => {
  const isMobile = useIsMobile();
  // Phones: the filter grid starts folded so the history list is visible first.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [actorOption, setActorOption] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 25, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadHistory = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getCrmHistory({ ...filters, page, limit: pagination.limit });
      const result = response?.data || response;
      setEvents(result?.events || []);
      setPagination(result?.pagination || { total: 0, page, limit: 25, totalPages: 0 });
    } catch (err) {
      setEvents([]);
      setError(err.message || 'Could not load CRM edit history.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(loadHistory, 200);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page]);

  const updateFilter = (event) => {
    const { name, value } = event.target;
    setPage(1);
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const clearFilters = () => {
    setFilters(INITIAL_FILTERS);
    setActorOption(null);
    setPage(1);
  };

  const loadActorOptions = async (search) => {
    try {
      const response = await searchCrmAssignableStaff(search);
      const result = response?.data || response;
      return (result?.users || []).map((user) => ({
        value: user.id,
        label: user.name || user.email || `User #${user.id}`,
      }));
    } catch (_) {
      return [];
    }
  };

  const columns = useMemo(() => [
    {
      name: 'Date and time',
      selector: (row) => row.changedAt || '',
      cell: (row) => row.changedAt ? new Date(row.changedAt).toLocaleString('en-IN') : '—',
      minWidth: '165px',
    },
    {
      name: 'Lead',
      selector: (row) => row.lead?.displayName || `Lead #${row.leadId}`,
      cell: (row) => row.lead ? (
        <div>
          <Link className="crm-history-lead" to={`/crm/leads/${row.leadId}`}>
            {row.lead.displayName || `Lead #${row.leadId}`}
          </Link>
          <div className="crm-muted">L-{String(row.leadId).padStart(5, '0')}{row.lead.archived ? ' · Archived' : ''}</div>
        </div>
      ) : (
        <div>
          <strong>Lead #{row.leadId}</strong>
          <div className="crm-muted">Deleted</div>
        </div>
      ),
      minWidth: '190px',
    },
    {
      name: 'Change',
      selector: (row) => `${row.entity} ${row.field}`,
      cell: (row) => (
        <div>
          <strong>{humanize(row.field)}</strong>
          <div className="crm-muted">{humanize(row.entity)}{row.entityId ? ` #${row.entityId}` : ''}</div>
        </div>
      ),
      minWidth: '170px',
    },
    {
      name: 'Before',
      cell: (row) => <div className="crm-history-value" title={displayValue(row.oldValue)}>{displayValue(row.oldValue)}</div>,
      minWidth: '220px',
      grow: 2,
    },
    {
      name: 'After',
      cell: (row) => <div className="crm-history-value" title={displayValue(row.newValue)}>{displayValue(row.newValue)}</div>,
      minWidth: '220px',
      grow: 2,
    },
    {
      name: 'Changed by',
      selector: (row) => row.changedBy?.name || row.changedBy?.email || `User #${row.changedBy?.id}`,
      cell: (row) => (
        <div>
          <strong>{row.changedBy?.name || `User #${row.changedBy?.id}`}</strong>
          {row.changedBy?.email && <div className="crm-muted">{row.changedBy.email}</div>}
        </div>
      ),
      minWidth: '185px',
    },
  ], []);

  return (
    <div className="container">
      <div className="crm-list-header">
        <div>
          <h2 className="page-title" style={{ margin: 0 }}>CRM edit history</h2>
          <div className="crm-list-count">{pagination.total} recorded change{pagination.total === 1 ? '' : 's'}</div>
        </div>
        <Link to="/crm/leads" className="btn btn-secondary">← Leads</Link>
      </div>

      <div className={`filters-card crm-history-filters${isMobile && !filtersOpen ? ' mobile-collapsed' : ''}`}>
        <div className="crm-filter-card-header">
          <div>
            <div className="crm-filter-card-title">Filter history</div>
            <div className="crm-filter-card-subtitle">Review who changed a Lead, what changed and when.</div>
          </div>
          {isMobile && (
            <button type="button" className="btn btn-secondary mobile-filters-toggle" onClick={() => setFiltersOpen((open) => !open)}>
              {filtersOpen ? 'Hide filters' : 'Filters'}
            </button>
          )}
          <button type="button" className="btn btn-secondary" onClick={clearFilters}>Clear</button>
        </div>
        <div className="crm-history-filter-grid">
          <input type="number" min="1" name="lead_id" className="form-control"
            value={filters.lead_id} onChange={updateFilter} placeholder="Lead ID" />
          <select name="entity" className="form-control" value={filters.entity} onChange={updateFilter}>
            <option value="">All change types</option>
            {ENTITY_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <input type="text" name="field" className="form-control"
            value={filters.field} onChange={updateFilter} placeholder="Field, e.g. stage" />
          <AsyncSelect cacheOptions defaultOptions isClearable loadOptions={loadActorOptions}
            value={actorOption} placeholder="Changed by"
            menuPortalTarget={document.body} styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            onChange={(option) => {
              setActorOption(option || null);
              setPage(1);
              setFilters((current) => ({ ...current, actor_id: option ? option.value : '' }));
            }} />
          <label className="crm-history-date-filter">
            <span>From</span>
            <input type="date" name="date_from" className="form-control" value={filters.date_from} onChange={updateFilter} />
          </label>
          <label className="crm-history-date-filter">
            <span>To</span>
            <input type="date" name="date_to" className="form-control" value={filters.date_to} onChange={updateFilter} />
          </label>
        </div>
      </div>

      {error && <div className="crm-form-error crm-history-error">{error}</div>}
      <div className="card crm-history-table-card">
        <DataTable columns={columns} data={events} progressPending={loading}
          pagination paginationServer paginationTotalRows={pagination.total}
          paginationDefaultPage={page} paginationPerPage={pagination.limit}
          onChangePage={setPage} highlightOnHover striped
          noDataComponent={<div style={{ padding: '28px' }}>No edit history found.</div>} />
      </div>
    </div>
  );
};

export default CrmHistory;
